import { Box, Flex, Typography } from '@project44/manifest-ds';

import { scopedT } from '../../../../utils/functions/scopedT';
import PredictedIcon from '../../PredictedTimestamp/Icons/PredictedIcon';

const t = scopedT();
import {
  DateTimeWrapper,
  DiffDays,
  Label,
  StackedFormatWrapper,
  Timezone,
} from '../../styles';

interface StackedFormatProps {
  dateDiff?: React.ReactNode | React.ReactNode[];
  diffDays: number;
  endHourMinute: string;
  hasTimeWindow: boolean;
  isPrediction?: boolean;
  isStale?: boolean;
  label: string;
  monthDayYear: string;
  startHourMinute: string;
  timeZone?: string;
  timezoneAbbreviation?: string;
}

/**
 *
 * @param dateDiff: a component to  be displayed next to the label: "Arrived -28 days"
 * @param diffDays: number of  days diff to be displayed as part of the time window: "Feb 23 · 23:00-02:00 +1"
 * @param endHourMinute: second part of a time window: "23:00-02:00"
 * @param hasTimeWindow: boolean, determines which layout is displayed
 * @param isPrediction: boolean, determines styling for predictions
 * @param isStale: boolean, determines styling for predictions
 * @param label: the timestamp label (header): "Arrived", "Planned Arrival"
 * @param monthDayYear: date/time formated as "Sep 29, 2023"
 * @param startHourMinute: first part of time window: 23:00-02:00"
 * @param timezoneAbbreviation: abbreviation: "CDT", etc.
 */
export const StackedFormat = ({
  dateDiff,
  diffDays,
  endHourMinute,
  hasTimeWindow,
  isPrediction,
  isStale,
  label,
  monthDayYear,
  startHourMinute,
  timezoneAbbreviation,
}: StackedFormatProps) => {
  const isPlannedTimestampWithoutData =
    label === t('overviewLabel.plannedArrival') && !startHourMinute;
  const isPredictionWithoutData = isPrediction && !startHourMinute;

  const getNoDataMessage = () => {
    if (isPlannedTimestampWithoutData) {
      return t('pendingCarrierUpdate');
    }

    if (isPredictionWithoutData) {
      return t('pendingCalculation');
    }

    return null;
  };

  const noDataMessage = getNoDataMessage();
  const isStalePrediction = isStale && isPrediction && !isPredictionWithoutData;

  return (
    <Flex orientation="vertical" align="start" data-testid="stacked-format">
      <Label data-testid="label" css={{ display: 'flex' }}>
        {label}
        {dateDiff}
      </Label>
      {hasTimeWindow ? (
        <StackedFormatWrapper
          orientation="horizontal"
          data-testid="with-window"
          isPrediction={isPrediction && !isPredictionWithoutData}
          isStale={isStalePrediction}
          hasNoData={isPrediction && isPredictionWithoutData}
        >
          {isPrediction && !isPredictionWithoutData && (
            <Box css={{ padding: '4px' }} data-testid="icon">
              <PredictedIcon isStale={isStalePrediction} />
            </Box>
          )}
          <Flex orientation="vertical">
            <Typography variant="bodyBold" data-testid="month-day-year">
              {monthDayYear}
            </Typography>
            <Typography variant="subtext" css={{ color: '$text-secondary' }}>
              <DateTimeWrapper as="span" data-testid="time">
                {startHourMinute}-{endHourMinute}
                {diffDays > 0 && (
                  <DiffDays data-testid="diff-days">{`+${diffDays}`}</DiffDays>
                )}
                {timezoneAbbreviation && (
                  <Timezone data-testid="timezone" addPadding={diffDays > 0}>
                    {' '}
                    {timezoneAbbreviation}
                  </Timezone>
                )}
              </DateTimeWrapper>
            </Typography>
          </Flex>
        </StackedFormatWrapper>
      ) : (
        <StackedFormatWrapper
          orientation="horizontal"
          data-testid="no-window"
          isPrediction={isPrediction && !isPredictionWithoutData}
          isStale={isStalePrediction}
        >
          {isPrediction && !isPredictionWithoutData && (
            <Box css={{ padding: '4px 4px 4px 0' }}>
              <PredictedIcon isStale={isStalePrediction} />
            </Box>
          )}
          {noDataMessage ? (
            <Flex orientation="vertical">
              <Typography variant="bodyBold" data-testid="month-day-year">
                {noDataMessage}
              </Typography>
            </Flex>
          ) : (
            <Flex orientation="vertical">
              <Typography variant="bodyBold" data-testid="month-day-year">
                {monthDayYear}
              </Typography>
              <Typography
                variant="subtext"
                data-testid="time-timezone"
                css={{ color: '$text-secondary' }}
              >
                <span>
                  {startHourMinute || '-'}{' '}
                  {startHourMinute ? timezoneAbbreviation : ''}
                </span>
              </Typography>
            </Flex>
          )}
        </StackedFormatWrapper>
      )}
    </Flex>
  );
};

export default StackedFormat;
